import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class LivespaceService {
  private apiUrl = environment.api.url;
  token: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private refreshToken(): void {
    this.authService.token().subscribe((token) => {
      this.token = token;
    });
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Accept-Language': 'en',
      Authorization: `Bearer ${this.token}`,
    });
  }

  /**
   * Connect Livespace — sends subdomain + API key + secret to backend for validation.
   */
  connect(subdomain: string, apiKey: string, apiSecret: string): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/connect`;
    this.refreshToken();
    return this.http.post<any>(url, {
      subdomain,
      api_key: apiKey,
      api_secret: apiSecret,
    }, { headers: this.getHeaders() });
  }

  /**
   * Disconnect Livespace.
   */
  disconnect(): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/disconnect`;
    this.refreshToken();
    return this.http.post<any>(url, {}, { headers: this.getHeaders() });
  }

  /**
   * Get Livespace contact by search ID (mirrors showContact for HubSpot).
   */
  showContact(searchId: number): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/contact?search_id=${searchId}`;
    this.refreshToken();
    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  /**
   * Get Livespace companies (for company dropdown).
   */
  getCompanies(searchId: number): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/companies?search_id=${searchId}`;
    this.refreshToken();
    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  /**
   * Get Livespace processes (equivalent to HubSpot pipelines).
   */
  getProcesses(): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/processes`;
    this.refreshToken();
    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  /**
   * Create a new contact in Livespace.
   */
  newContact(
    userId: any,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    companyName: string,
    jobTitle: string,
    emails?: string[],
    phones?: string[],
    socialMedia?: { linkedin?: string; twitter?: string; facebook?: string },
    companyDetails?: {
      domain?: string;
      phone?: string;
      email?: string;
      city?: string;
      state?: string;
      country?: string;
      street?: string;
      postcode?: string;
      linkedin?: string;
      twitter?: string;
      facebook?: string;
    }
  ): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/new/contact`;
    this.refreshToken();
    return this.http.post<any>(url, {
      user_id: userId,
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      emails: emails ?? [email].filter(Boolean),
      phones: phones ?? [phone].filter(Boolean),
      company_name: companyName,
      job_title: jobTitle,
      linkedin: socialMedia?.linkedin || '',
      twitter: socialMedia?.twitter || '',
      facebook: socialMedia?.facebook || '',
      // Full company details from the report, so the backend can create the
      // linked company with all its data instead of just the name.
      company_domain: companyDetails?.domain || '',
      company_phone: companyDetails?.phone || '',
      company_email: companyDetails?.email || '',
      company_city: companyDetails?.city || '',
      company_state: companyDetails?.state || '',
      company_country: companyDetails?.country || '',
      company_street: companyDetails?.street || '',
      company_postcode: companyDetails?.postcode || '',
      company_linkedin: companyDetails?.linkedin || '',
      company_twitter: companyDetails?.twitter || '',
      company_facebook: companyDetails?.facebook || '',
    }, { headers: this.getHeaders() });
  }

  /**
   * Create a new company in Livespace.
   */
  newCompany(
    userId: any,
    companyName: string,
    companyDomain: string,
    companyPhone: string,
    companyCity: string,
    companyState: string,
    companyEmail?: string,
    companyLinkedin?: string,
    companyTwitter?: string,
    companyFacebook?: string,
    companyClientStatus?: string
  ): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/new/company`;
    this.refreshToken();
    return this.http.post<any>(url, {
      user_id: userId,
      company_name: companyName,
      company_domain: companyDomain,
      company_phone: companyPhone,
      company_city: companyCity,
      company_state: companyState,
      company_email: companyEmail,
      company_linkedin: companyLinkedin,
      company_twitter: companyTwitter,
      company_facebook: companyFacebook,
      company_client_status: companyClientStatus,
    }, { headers: this.getHeaders() });
  }

  /**
   * Update an existing contact in Livespace.
   */
  updateContact(
    userId: any,
    contactId: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    companyName: string,
    jobTitle: string,
    emails?: string[],
    phones?: string[],
    socialMedia?: { linkedin?: string; twitter?: string; facebook?: string }
  ): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/update/contact`;
    this.refreshToken();
    return this.http.put<any>(url, {
      user_id: userId,
      contact_id: contactId,
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      emails: emails ?? [email].filter(Boolean),
      phones: phones ?? [phone].filter(Boolean),
      company_name: companyName,
      job_title: jobTitle,
      linkedin: socialMedia?.linkedin || '',
      twitter: socialMedia?.twitter || '',
      facebook: socialMedia?.facebook || '',
    }, { headers: this.getHeaders() });
  }

  /**
   * Update an existing company in Livespace.
   */
  updateCompany(
    userId: any,
    companyId: string,
    companyName: string,
    companyDomain: string,
    companyPhone: string,
    companyCity: string,
    companyState: string,
    companyEmail?: string,
    companyLinkedin?: string,
    companyTwitter?: string,
    companyFacebook?: string
  ): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/update/company`;
    this.refreshToken();
    return this.http.put<any>(url, {
      user_id: userId,
      company_id: companyId,
      company_name: companyName,
      company_domain: companyDomain,
      company_phone: companyPhone,
      company_city: companyCity,
      company_state: companyState,
      company_email: companyEmail,
      company_linkedin: companyLinkedin,
      company_twitter: companyTwitter,
      company_facebook: companyFacebook,
    }, { headers: this.getHeaders() });
  }

  /**
   * Create a new deal in Livespace.
   */
  newDeal(
    userId: any,
    dealName: string,
    companyName: string,
    contactName: string,
    processId?: string,
    dateEnd?: string,
    sourceId?: string,
    ownerId?: string,
    visibility?: string,
    categoryName?: string,
    productId?: string,
    price?: string
  ): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/new/deal`;
    this.refreshToken();
    return this.http.post<any>(url, {
      user_id: userId,
      deal_name: dealName,
      company_name: companyName,
      contact_name: contactName,
      process_id: processId,
      date_end: dateEnd,
      source_id: sourceId,
      owner_id: ownerId,
      visibility,
      category_name: categoryName,
      product_id: productId,
      price,
    }, { headers: this.getHeaders() });
  }

  /**
   * Get Livespace deals for a contact/company.
   */
  getDeals(contactId?: string, companyId?: string): Observable<any> {
    let url = `${this.apiUrl}/oauth/livespace/deals?`;
    if (contactId) url += `contact_id=${contactId}&`;
    if (companyId) url += `company_id=${companyId}&`;
    this.refreshToken();
    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  /**
   * Get Livespace deal sources.
   */
  getSources(): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/sources`;
    this.refreshToken();
    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  /**
   * Get all Livespace users (team members).
   */
  getUsers(): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/users`;
    this.refreshToken();
    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  /**
   * Get Livespace products/services.
   */
  getProducts(): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/products`;
    this.refreshToken();
    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  /**
   * Get current Livespace user info (for owner auto-fill).
   */
  getCurrentUser(): Observable<any> {
    const url = `${this.apiUrl}/oauth/livespace/current-user`;
    this.refreshToken();
    return this.http.get<any>(url, { headers: this.getHeaders() });
  }
}
